import { COMMON_CATEGORIES, type SourceType, type Strain } from '@shared';

export const ACTIVE_PRODUCT_CATEGORIES = [
  'Flower',
  'Pre-Rolls',
  'Oral Extracts',
  'Edibles, Infusibles, & Beverages',
  '510 Thread Cartridges',
  'AIO Disposables',
  'Loose Concentrates',
] as const;

export const LEGACY_OR_DORMANT_PRODUCT_CATEGORIES = COMMON_CATEGORIES.filter(
  (category) =>
    !(ACTIVE_PRODUCT_CATEGORIES as readonly string[]).includes(category),
);

export const ACTIVE_STRAIN_CLASSIFICATIONS = [
  'Sativa',
  'Sativa Dominant Hybrid',
  'Hybrid',
  'Blend',
  'Indica Dominant Hybrid',
  'Indica',
] as const;

export function isActiveProductCategory(value: string): boolean {
  return (ACTIVE_PRODUCT_CATEGORIES as readonly string[]).includes(value);
}

export function isActiveStrainClassification(value: string): boolean {
  return (ACTIVE_STRAIN_CLASSIFICATIONS as readonly string[]).includes(value);
}

export const OPTIONAL_PRODUCT_FIELDS = [
  { key: 'cbnPercent', label: 'CBN %' },
  { key: 'cbgPercent', label: 'CBG %' },
  { key: 'thcvPercent', label: 'THCV %' },
  { key: 'cbcPercent', label: 'CBC %' },
  { key: 'totalTerpenesPercent', label: 'Total Terpene %' },
] as const;

export type OptionalProductFieldKey =
  (typeof OPTIONAL_PRODUCT_FIELDS)[number]['key'];

export type OptionalProductFieldDraft = Record<OptionalProductFieldKey, string>;

export interface StrainFormState {
  productName: string;
  brand: string;
  strainClassSourceText: string;
  lineageSourceText: string;
  thcPercent: string;
  cbdPercent: string;
  cbnPercent: string;
  cbgPercent: string;
  thcvPercent: string;
  cbcPercent: string;
  totalTerpenesPercent: string;
  notes: string;
  producer: string;
  category: string;
  cultivarType: string;
  packageDate: string;
  purchaseDate: string;
  lotNumber: string;
}

export function deriveProductDisplayName(
  brand: string,
  productName: string,
): string | undefined {
  const normalizedBrand = brand.trim();
  const normalizedProductName = productName.trim();
  if (normalizedBrand && normalizedProductName) {
    return `${normalizedBrand} - ${normalizedProductName}`;
  }
  return normalizedProductName || normalizedBrand || undefined;
}

export function toStrainFormState(
  initial?: Partial<Strain>,
): StrainFormState {
  const num = (value: number | null | undefined): string =>
    value !== undefined && value !== null ? String(value) : '';
  return {
    productName: initial?.productName ?? '',
    brand: initial?.brand ?? '',
    strainClassSourceText:
      initial?.strainClassSourceText ?? initial?.cultivarType ?? '',
    lineageSourceText: initial?.lineageSourceText ?? '',
    thcPercent: num(initial?.thcPercent),
    cbdPercent: num(initial?.cbdPercent),
    cbnPercent: num(initial?.cbnPercent),
    cbgPercent: num(initial?.cbgPercent),
    thcvPercent: num(initial?.thcvPercent),
    cbcPercent: num(initial?.cbcPercent),
    totalTerpenesPercent: num(initial?.totalTerpenesPercent),
    notes: initial?.notes ?? '',
    producer: initial?.producer ?? '',
    category: initial?.category ?? '',
    cultivarType: initial?.cultivarType ?? '',
    packageDate: initial?.packageDate ?? '',
    purchaseDate: initial?.purchaseDate ?? '',
    lotNumber: initial?.lotNumber ?? '',
  };
}

export function initialVisibleOptionalFields(
  initial?: Partial<Strain>,
): Set<OptionalProductFieldKey> {
  return new Set(
    OPTIONAL_PRODUCT_FIELDS.filter(
      ({ key }) => initial?.[key] !== null && initial?.[key] !== undefined,
    ).map(({ key }) => key),
  );
}

export function hideOptionalField(
  visible: ReadonlySet<OptionalProductFieldKey>,
  key: OptionalProductFieldKey,
): Set<OptionalProductFieldKey> {
  const next = new Set(visible);
  next.delete(key);
  return next;
}

export function clearOptionalField(
  state: StrainFormState,
  key: OptionalProductFieldKey,
): StrainFormState {
  return { ...state, [key]: '' };
}

export function createOptionalFieldDraft(
  state: StrainFormState,
): OptionalProductFieldDraft {
  return Object.fromEntries(
    OPTIONAL_PRODUCT_FIELDS.map(({ key }) => [key, state[key]]),
  ) as OptionalProductFieldDraft;
}

export function validateOptionalFieldDraft(
  draft: OptionalProductFieldDraft,
): Partial<Record<OptionalProductFieldKey, string>> {
  const errors: Partial<Record<OptionalProductFieldKey, string>> = {};
  for (const { key, label } of OPTIONAL_PRODUCT_FIELDS) {
    const raw = draft[key].trim();
    if (raw === '') continue;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      errors[key] = `${label} must be between 0 and 100.`;
    }
  }
  return errors;
}

export function applyOptionalFieldDraft(
  state: StrainFormState,
  visible: ReadonlySet<OptionalProductFieldKey>,
  draft: OptionalProductFieldDraft,
): {
  state: StrainFormState;
  visible: Set<OptionalProductFieldKey>;
} {
  const nextState = { ...state };
  const nextVisible = new Set(visible);
  for (const { key } of OPTIONAL_PRODUCT_FIELDS) {
    const value = draft[key].trim();
    if (value === '') continue;
    nextState[key] = value;
    nextVisible.add(key);
  }
  return { state: nextState, visible: nextVisible };
}

function numOrUndef(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  const number = Number(trimmed);
  return Number.isFinite(number) ? number : NaN;
}

export function buildStrainFormCandidate(
  state: StrainFormState,
  initial: Partial<Strain> | undefined,
  forceSourceType: SourceType,
) {
  return {
    name: deriveProductDisplayName(state.brand, state.productName),
    productName: state.productName.trim() || undefined,
    producer: state.producer.trim() || undefined,
    brand: state.brand.trim() || undefined,
    category: state.category.trim() || undefined,
    cultivarType:
      state.cultivarType.trim() ||
      state.strainClassSourceText.trim() ||
      undefined,
    strainClassSourceText: state.strainClassSourceText.trim() || undefined,
    lineageSourceText: state.lineageSourceText.trim() || undefined,
    thcPercent: numOrUndef(state.thcPercent),
    cbdPercent: numOrUndef(state.cbdPercent),
    cbnPercent: numOrUndef(state.cbnPercent),
    cbgPercent: numOrUndef(state.cbgPercent),
    thcvPercent: numOrUndef(state.thcvPercent),
    cbcPercent: numOrUndef(state.cbcPercent),
    totalTerpenesPercent: numOrUndef(state.totalTerpenesPercent),
    packageDate: state.packageDate || undefined,
    purchaseDate: state.purchaseDate || undefined,
    lotNumber: state.lotNumber.trim() || undefined,
    notes: state.notes.trim() || undefined,
    referenceNotes: initial?.referenceNotes ?? undefined,
    sourceType: forceSourceType,
    isVerified: initial?.isVerified ?? false,
  };
}
